import { api, toast, inspectorForm, openModal, closeModal } from '../state.js';
import { esc, badge, formatDateTime } from '/core/js/utils.js';
import qrcode from '../vendor/qrcode.js';

/**
 * <arc-two-step> — your own two-step sign-in, on My account (plan H.1). Opt
 * in: off until you turn it on.
 *
 *   off  "Turn on…": a drawer with the QR code (drawn here, from the key the
 *        API sends — it never goes to an outside service), the key as text
 *        for typing in, and a box for the first code. That code turns it on,
 *        and the recovery codes are shown, once.
 *   on   since when and recovery codes left; "New recovery codes…" and
 *        "Turn off…", each of which asks for a code first.
 *
 * Everything goes through /me/two-step, which takes the account from the
 * session. Someone else's is turned off from Users.
 */
class ArcTwoStep extends HTMLElement {
  connectedCallback() {
    this.innerHTML = `<div class="card"><div class="card-header"><span>Two-step sign-in</span></div>
      <div class="card-body"><div class="loading-center"><div class="spinner"></div></div></div></div>`;
    this.addEventListener('click', e => {
      const act = e.target.closest('[data-two-step]')?.dataset.twoStep;
      if (act === 'on') this.#turnOn();
      else if (act === 'codes') this.#newCodes();
      else if (act === 'off') this.#turnOff();
    });
    this.#load();
  }

  async #load() {
    try {
      this.#render(await api.me.twoStep.get());
    } catch (err) {
      this.querySelector('.card-body').innerHTML = `<div class="alert alert-danger">${esc(err.message)}</div>`;
    }
  }

  #render(s) {
    const low = s.on && s.recovery_codes_left <= 3;
    this.innerHTML = `
      <div class="card">
        <div class="card-header"><span>Two-step sign-in</span>${s.on ? badge('active', 'on') : badge('none', 'off')}</div>
        <div class="card-body">
          ${s.on ? `
            <dl class="kv">
              <dt>On since</dt><dd class="mono">${esc(formatDateTime(s.since))}</dd>
              <dt>Recovery codes</dt><dd>${s.recovery_codes_left} left</dd>
            </dl>
            ${low ? `<p class="alert alert-warning card-block">${s.recovery_codes_left === 0 ? 'No recovery codes left.' : 'Only a few recovery codes left.'}
              Make a new set while you can still sign in.</p>` : ''}
            <p class="text-muted card-block">Every sign-in to ARC asks for a code from your authenticator app
              after your password. Lost the phone? A recovery code works in its place, once each.</p>
            <div class="btn-row card-block">
              <button type="button" class="btn btn-secondary" data-two-step="codes">New recovery codes…</button>
              <button type="button" class="btn btn-danger-ghost" data-two-step="off">Turn off…</button>
            </div>` : `
            <p class="text-muted">Off: you sign in with your password alone. Turned on, signing in to ARC
              also takes a 6-digit code from an app on your phone — Google Authenticator, Microsoft Authenticator,
              1Password or any other. No account with Google or Microsoft is needed. Your email, file shares and
              Wi-Fi are not affected.</p>
            <button type="button" class="btn btn-primary card-block" data-two-step="on">Turn on…</button>`}
        </div>
      </div>`;
  }

  async #turnOn() {
    let setup;
    try {
      setup = await api.me.twoStep.setup();
    } catch (err) {
      toast(err.message ?? 'Could not start setting it up', 'error');
      return;
    }
    inspectorForm({
      title: 'Turn on two-step sign-in',
      width: 460,
      submitLabel: 'Turn on',
      body: `
        <ol class="two-step-steps">
          <li>In your authenticator app, add an account and choose to scan a QR code.</li>
          <li>Scan this:
            <div class="two-step-qr">${qrSvg(setup.uri)}</div>
            <p class="field-hint">Can't scan it? Enter this key instead, as a time-based key:</p>
            <p class="mono two-step-key">${esc(setup.secret)}</p></li>
          <li>The app now lists ${esc(setup.issuer)}. Enter the 6-digit code it shows:
            <label class="form-field"><span>Code</span>
              <input name="code" class="mono" inputmode="numeric" autocomplete="one-time-code"
                     maxlength="7" required></label></li>
        </ol>`,
      onSubmit: async (f) => {
        const res = await api.me.twoStep.confirm(f.code.value.trim());
        f.code.value = '';
        this.#render(res);
        toast('Two-step sign-in is on', 'success');
        showRecoveryCodes(res.recovery_codes, 'Two-step sign-in is on. Your other sessions have been signed out.');
      },
    });
  }

  #newCodes() {
    inspectorForm({
      title: 'New recovery codes',
      submitLabel: 'Make new codes',
      body: `
        <p class="text-muted">Your old recovery codes stop working, and you get ten new ones.</p>
        ${codeField()}`,
      onSubmit: async (f) => {
        const res = await api.me.twoStep.recoveryCodes(f.code.value.trim());
        this.#render(res);
        showRecoveryCodes(res.recovery_codes, 'Your old recovery codes no longer work.');
      },
    });
  }

  #turnOff() {
    inspectorForm({
      title: 'Turn off two-step sign-in',
      submitLabel: 'Turn off',
      body: `
        <p class="text-muted">After this, signing in to ARC takes your password alone. You can turn it on
          again at any time; the app's entry for ARC can be deleted.</p>
        ${codeField()}`,
      onSubmit: async (f) => {
        this.#render(await api.me.twoStep.off(f.code.value.trim()));
        toast('Two-step sign-in is off', 'success');
      },
    });
  }
}

/** The code box for a change to two-step itself: the app's code or a recovery code. */
function codeField() {
  return `
    <label class="form-field"><span>A code from your app, or a recovery code</span>
      <input name="code" class="mono" autocomplete="one-time-code" autocapitalize="off" spellcheck="false"
             maxlength="20" required></label>`;
}

/**
 * The QR code as an inline SVG. Black on white whatever the theme — scanners
 * want dark modules on a light ground with a quiet zone — so its colours are
 * the code's own, not palette tokens.
 */
function qrSvg(text) {
  const qr = qrcode(0, 'M');
  qr.addData(text);
  qr.make();
  const n = qr.getModuleCount();
  const quiet = 4;
  let d = '';
  for (let r = 0; r < n; r++) {
    for (let c = 0; c < n; c++) if (qr.isDark(r, c)) d += `M${c + quiet},${r + quiet}h1v1h-1z`;
  }
  const size = n + quiet * 2;
  return `<svg viewBox="0 0 ${size} ${size}" role="img" aria-label="QR code for your authenticator app"
    shape-rendering="crispEdges" xmlns="http://www.w3.org/2000/svg">
    <rect width="${size}" height="${size}" fill="#fff"/><path d="${d}" fill="#000"/></svg>`;
}

/** The recovery codes, the one time they are shown. */
function showRecoveryCodes(codes, lead) {
  const onClick = async (e) => {
    const action = e.target.dataset.action;
    if (action === 'close') closeModal();
    if (action === 'copy') {
      try {
        await navigator.clipboard.writeText(codes.join('\n') + '\n');
        toast('Copied', 'success');
      } catch {
        toast('Could not copy — select them instead', 'error');
      }
    }
  };
  // The modal container is shared, so the listener goes with this modal.
  const { container } = openModal(`
    <div class="modal-header"><span>Your recovery codes</span>
      <button class="modal-close" data-action="close">&times;</button></div>
    <div class="modal-body">
      <p>${esc(lead)}</p>
      <p class="text-muted">If you lose your phone, each of these signs you in once in place of a code.
        Print them, or keep them in your password manager — they are not shown again.</p>
      <ul class="two-step-codes mono">${codes.map(c => `<li>${esc(c)}</li>`).join('')}</ul>
    </div>
    <div class="modal-footer">
      <button class="btn btn-secondary" data-action="copy">Copy</button>
      <button class="btn btn-primary" data-action="close">I have saved them</button>
    </div>`, { onClose: () => container.removeEventListener('click', onClick) });
  container.addEventListener('click', onClick);
}

customElements.define('arc-two-step', ArcTwoStep);
